// v2.0.0 - 2026-01-31 - Upload page with simplified 2-step wizard

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/stores/authStore'
import { api } from '@/lib/api/client'
import { parseGSEFile } from '@/lib/excel-parser'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { FileUploader, FilePreview } from '@/components/admin'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

type Step = 'upload' | 'complete'

interface ParsedFileData {
  type: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN'
  rows: number
  columns: string[]
  preview: Record<string, unknown>[]
  errors: string[]
  warnings: string[]
  rawData: unknown[]
}

interface Configuration {
  id: string
  name: string
  crin: string
  zona: string
  memberCount: number
}

export default function UploadPage() {
  const { user } = useAuthStore()
  const [step, setStep] = useState<Step>('upload')
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<ParsedFileData | null>(null)
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7))
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadResult, setUploadResult] = useState<{
    recordsCreated: number
    recordsUpdated: number
  } | null>(null)

  // Configurations
  const [configs, setConfigs] = useState<Configuration[]>([])
  const [loadingConfigs, setLoadingConfigs] = useState(true)

  useEffect(() => {
    if (user?.cer_id) {
      loadConfigurations()
    }
  }, [user?.cer_id])

  const loadConfigurations = async () => {
    if (!user?.cer_id) return
    setLoadingConfigs(true)
    const result = await api.getConfigurations(user.cer_id)
    if (result.success) {
      const data = 'data' in result && result.data ? result.data : []
      setConfigs(data)
      // Auto-select first config
      if (data.length > 0) {
        setSelectedConfig(data[0].id)
      }
    }
    setLoadingConfigs(false)
  }

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file)
    setError(null)

    try {
      const result = await parseGSEFile(file)

      const data: ParsedFileData = {
        type: result.type,
        rows: 0,
        columns: [],
        preview: [],
        errors: [],
        warnings: [],
        rawData: [],
      }

      if (result.type === 'CORRISPETTIVI' && result.corrispettivi?.success && result.corrispettivi.data) {
        const corrData = result.corrispettivi.data
        data.rows = corrData.length
        data.columns = Object.keys(corrData[0] || {})
        data.preview = corrData.slice(0, 5) as unknown as Record<string, unknown>[]
        data.rawData = corrData
        data.errors = result.corrispettivi.errors
        data.warnings = result.corrispettivi.warnings
      } else if (result.type === 'MISURE' && result.misure?.success && result.misure.data) {
        const misureData = result.misure.data
        data.rows = misureData.length
        data.columns = ['pod', 'date', 'hour', 'tipo', 'energia_kwh']
        data.preview = misureData.slice(0, 5).map((m) => ({
          pod: m.pod,
          date: m.date,
          hour: m.hour,
          tipo: m.tipo,
          energia_kwh: m.energia_kwh,
        }))
        data.rawData = misureData
        data.errors = result.misure.errors
        data.warnings = result.misure.warnings
      } else if (result.type === 'RIEPILOGO' && result.riepilogo?.success && result.riepilogo.data) {
        const riepilogoData = result.riepilogo.data
        data.rows = riepilogoData.length
        data.columns = Object.keys(riepilogoData[0] || {})
        data.preview = riepilogoData.slice(0, 5) as unknown as Record<string, unknown>[]
        data.rawData = riepilogoData
        data.errors = result.riepilogo.errors
        data.warnings = result.riepilogo.warnings
      } else {
        data.errors = ['Formato file non riconosciuto']
      }

      setParsedData(data)
    } catch (err) {
      setParsedData({
        type: 'UNKNOWN',
        rows: 0,
        columns: [],
        preview: [],
        errors: [(err as Error).message],
        warnings: [],
        rawData: [],
      })
    }
  }

  const handleConfirmUpload = async () => {
    if (!selectedFile || !parsedData || !selectedConfig) return

    setIsProcessing(true)
    setError(null)

    try {
      // Upload file first
      const uploadRes = await api.uploadFile(selectedFile, selectedConfig)

      // Then process data
      const processRes = await api.processUpload(uploadRes.id) as { recordsCreated: number; recordsUpdated: number }

      setUploadResult({
        recordsCreated: processRes.recordsCreated || 0,
        recordsUpdated: processRes.recordsUpdated || 0,
      })
      setStep('complete')
    } catch (err) {
      setError((err as Error).message || 'Errore durante l\'elaborazione')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReset = () => {
    setStep('upload')
    setSelectedFile(null)
    setParsedData(null)
    setUploadResult(null)
    setError(null)
  }

  const selectedConfigData = configs.find((c) => c.id === selectedConfig)

  return (
    <PageContainer>
      <PageHeader
        title="Carica Dati Energia"
        description="Importa file Excel GSE con dati energia o corrispettivi"
      />

      <div className="space-y-6">
        {/* Step indicator - simplified to 2 steps */}
        <div className="flex gap-2">
          {['upload', 'complete'].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-[var(--color-accent)] text-white'
                    : i < ['upload', 'complete'].indexOf(step)
                      ? 'bg-[var(--color-success)] text-white'
                      : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]'
                }`}
              >
                {i + 1}
              </div>
              <span className="text-sm text-[var(--color-text-secondary)] hidden sm:inline">
                {s === 'upload' && 'Upload & Anteprima'}
                {s === 'complete' && 'Completo'}
              </span>
              {i < 1 && <div className="w-8 h-0.5 bg-[var(--color-bg-tertiary)]" />}
            </div>
          ))}
        </div>

        {step === 'upload' && (
          <div className="space-y-4">
            {/* Config and Period selection */}
            <Card>
              <CardContent className="pt-6">
                {loadingConfigs ? (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                ) : configs.length === 0 ? (
                  <p className="text-[var(--color-text-secondary)]">Nessuna configurazione disponibile</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <label className="text-sm font-medium text-[var(--color-text-primary)] block mb-1">
                        Configurazione
                      </label>
                      <select
                        value={selectedConfig || ''}
                        onChange={(e) => setSelectedConfig(e.target.value)}
                        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm"
                      >
                        {configs.map((config) => (
                          <option key={config.id} value={config.id}>
                            {config.name} ({config.crin})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[var(--color-text-primary)] block mb-1">
                        Periodo
                      </label>
                      <input
                        type="month"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm"
                      />
                    </div>
                    {selectedConfigData && (
                      <div className="ml-auto">
                        <Badge variant="default">{selectedConfigData.zona}</Badge>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* File uploader - only show if config selected */}
            {selectedConfig && !parsedData && (
              <FileUploader onFileSelect={handleFileSelect} />
            )}

            {/* Preview - shows after file selected */}
            {parsedData && selectedFile && (
              <FilePreview
                filename={selectedFile.name}
                data={parsedData}
                onConfirm={handleConfirmUpload}
                onCancel={() => {
                  setSelectedFile(null)
                  setParsedData(null)
                }}
                isLoading={isProcessing}
              />
            )}

            {error && (
              <div className="rounded-md bg-[var(--color-bg-error)] p-4 text-sm text-[var(--color-error)]">
                {error}
              </div>
            )}
          </div>
        )}

        {step === 'complete' && uploadResult && (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-bg-success)] flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-[var(--color-success)]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-lg font-medium mb-2 text-[var(--color-text-primary)]">Upload completato!</p>
              <p className="text-[var(--color-text-secondary)] mb-4">
                {uploadResult.recordsCreated} nuovi record, {uploadResult.recordsUpdated} aggiornati
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={handleReset}>
                  Carica altro file
                </Button>
                <Button onClick={() => window.location.href = '/admin/calculations'}>
                  Vai ai calcoli
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  )
}
